/**
 * Icarus D&D redesign — the app's global shell. Replaces the previous
 * left sidebar with a slim, sticky 64px topbar per
 * `design_handoff_icarus_dnd/README.md` ("Topbar (global)").
 *
 * The old sidebar carried more than primary navigation: an org switcher,
 * an admin/settings section, a theme toggle, legal links and logout. The
 * design brief is explicit that this is a *restyle*, not a feature cut
 * ("Bestehende Architektur respektieren … nur Styling/Layout ändern"),
 * so none of that goes away — it folds into the avatar menu on the
 * right. Primary/secondary nav access rules are unchanged — this
 * component reads from the same `useMainNavRoutes`/`useAdminNavRoutes`
 * hooks that back the access-control tests.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LogOut, Command } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useArea } from '@/contexts/AreaContext';
import type { Area } from '@/features/area';
import { useOrganizationBranding } from '@/data';
import { useFeatures } from '@/hooks/useFeatures';
import { useMainNavRoutes, useAdminNavRoutes, isRouteActive } from '@/hooks/useNavRoutes';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { InsightBell } from '@/components/ai/InsightBell';
import { TopbarPrimaryNav } from '@/components/TopbarPrimaryNav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function areaSubtitle(area: Area): string {
  return area === 'klinik' ? 'Klinische Ernährungstherapie' : 'Ambulante Ernährungstherapie';
}

/** Opens the existing global CommandPalette by replaying the Cmd/Ctrl+K
 * keydown it already listens for — avoids adding an external-open prop
 * to a component that's otherwise fully self-contained. */
function openCommandPalette() {
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }),
  );
}

export function Topbar() {
  const { t } = useTranslation();
  const { profile, signOut } = useAuth();
  const { hasFeature } = useFeatures();
  const area = useArea();
  const navigate = useNavigate();
  const location = useLocation();
  const orgId = profile?.organization_id;
  const { data: orgBranding } = useOrganizationBranding(orgId ?? undefined);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const isPlatformAdminWorkspace = profile?.role === 'admin';
  const { primary, secondary, reorderPrimary } = useMainNavRoutes();
  const adminRoutes = useAdminNavRoutes();

  const roleLabel = profile?.role
    ? t(`roles.${profile.role}`, { defaultValue: profile.role })
    : '';

  const orgName = isPlatformAdminWorkspace
    ? 'Plattform-Verwaltung'
    : (orgBranding?.name ?? 'Icarus');

  // Läuft minütlich mit, damit Datum + Uhrzeit im Header nicht beim
  // ersten Render einfrieren (z.B. über Mitternacht hinweg).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const dateLabel = now.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeLabel = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="sticky top-0 z-20 flex h-16 items-center gap-8 border-b border-hairline bg-surface px-6 lg:px-12">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
      >
        {t('a11y.skip_to_content')}
      </a>

      <Link to="/" className="flex shrink-0 items-center gap-2.5">
        <img
          src={isPlatformAdminWorkspace ? '/icarus_logo_600px.webp' : (orgBranding?.logo_url ?? '/icarus_logo_600px.webp')}
          alt={orgName}
          className="h-10 w-10 rounded-full object-cover"
          width={40}
          height={40}
        />
        <span className="hidden items-baseline gap-2 sm:flex">
          <span className="font-serif text-base font-semibold tracking-[-0.01em] text-ink">{orgName}</span>
          {!isPlatformAdminWorkspace && (
            <span className="font-mono text-[11px] font-semibold tracking-[0.14em] text-ink-tertiary-2">
              D&amp;D
            </span>
          )}
        </span>
      </Link>

      <nav className="hidden flex-1 items-center gap-7 text-sm lg:flex" aria-label={t('a11y.skip_to_content')}>
        <TopbarPrimaryNav
          routes={primary}
          isActive={(route) => isRouteActive(route, location.pathname)}
          onReorder={reorderPrimary}
        />

        {secondary.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-ink-tertiary transition-colors hover:text-ink focus-visible:outline-none">
              {t('nav.more')}
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {secondary.map((route) => {
                const Icon = route.nav!.icon;
                return (
                  <DropdownMenuItem key={route.id} asChild>
                    <Link to={route.path} className="flex items-center gap-2">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {route.nav!.label}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </nav>

      <div className="ml-auto flex items-center gap-3.5">
        <span className="hidden flex-col items-end leading-tight font-mono text-ink-tertiary-2 md:flex">
          <span className="text-[12.5px] font-medium">{dateLabel}</span>
          <span className="text-[11px] text-ink-tertiary">{timeLabel}</span>
        </span>

        <button
          type="button"
          onClick={openCommandPalette}
          className="hidden items-center gap-1 rounded-md bg-[oklch(0.95_0.005_250)] px-2 py-1 font-mono text-[11px] font-medium text-ink-tertiary transition-colors hover:bg-hairline-soft sm:flex"
          aria-label={t('nav.openMenu')}
        >
          <Command className="h-3 w-3" aria-hidden="true" />K
        </button>

        {!isPlatformAdminWorkspace && hasFeature('ai_native') && <InsightBell />}

        <DropdownMenu>
          <DropdownMenuTrigger className="flex h-8.5 w-8.5 items-center justify-center rounded-full bg-[oklch(0.9_0.01_250)] font-sans text-[12.5px] font-semibold text-[oklch(0.42_0.02_250)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            {profile?.first_name?.[0]}
            {profile?.last_name?.[0]}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="truncate text-sm font-medium">
                  {profile?.first_name} {profile?.last_name}
                </span>
                <span className="text-xs font-normal text-muted-foreground">{roleLabel}</span>
              </div>
            </DropdownMenuLabel>
            {!isPlatformAdminWorkspace && (
              <DropdownMenuLabel className="pt-0 text-xs font-normal text-muted-foreground">
                {areaSubtitle(area)}
              </DropdownMenuLabel>
            )}
            <DropdownMenuSeparator />

            {adminRoutes.map((route) => {
              const Icon = route.nav!.icon;
              return (
                <DropdownMenuItem key={route.id} asChild>
                  <Link to={route.path} className="flex items-center gap-2">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {route.nav!.label}
                  </Link>
                </DropdownMenuItem>
              );
            })}

            <DropdownMenuSeparator />
            <div className="flex items-center justify-center gap-3 px-2 py-1.5 text-[10px] text-muted-foreground/70">
              <a href="/impressum" target="_blank" rel="noopener" className="hover:text-muted-foreground">
                Impressum
              </a>
              <span>|</span>
              <a href="/datenschutz" target="_blank" rel="noopener" className="hover:text-muted-foreground">
                Datenschutz
              </a>
              <span>|</span>
              <a href="/agb" target="_blank" rel="noopener" className="hover:text-muted-foreground">
                AGB
              </a>
            </div>
            <DropdownMenuSeparator />

            <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="flex items-center gap-2 text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('nav.logoutConfirmTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('nav.logoutConfirmBody')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={signOut}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('nav.logout')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {!isPlatformAdminWorkspace && (
              <div className="px-2 pt-1.5">
                <OrgSwitcher />
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
